import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { College, CollegeService } from 'us-web-services';
import { Button, ButtonType } from '../../../components';

const formatCollege = (c: College): string => {
  const city = c.city ? ` - ${c.city}` : '';
  const state = c.state ? `, ${c.state}` : '';
  return `${c.name ?? ''}${city}${state}`;
};

// Mirrors the search-by-education dropdown in sitters-search-filter-body.tpl.html.
// Typeahead against /v2/colleges using the same `name.containsIgnoreCase(...)
// .or(alias.containsIgnoreCase(...))` query the Angular controller builds
// (see sitters-search-filter-body.js:303-315). URL params: `collegeId` +
// `currentlyAttendingCollege`.
export const UniversityFilter = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<College[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selected, setSelected] = useState<College | null>(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const collegeId = searchParams.get('collegeId');
  const currentlyAttending = searchParams.get('currentlyAttendingCollege') === 'true';

  // On mount / collegeId change from URL, hydrate the selected college name.
  useEffect(() => {
    if (!collegeId) {
      setSelected(null);
      return;
    }
    const id = Number(collegeId);
    if (Number.isNaN(id)) return;
    const load = async (): Promise<void> => {
      try {
        const r = await CollegeService.get(id);
        const college = r.data?.data;
        if (college) setSelected(college);
      } catch {
        /* ignore */
      }
    };
    void load();
  }, [collegeId]);

  // Debounced autocomplete on typed query.
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    const handle = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      const escaped = query.replace(/"/g, '\\"');
      const q =
        `name.containsIgnoreCase("${escaped}")` +
        `.or(alias.containsIgnoreCase("${escaped}"))`;
      void (async (): Promise<void> => {
        try {
          const r = await CollegeService.getByFilter({ params: { q }, signal: controller.signal });
          setResults((r.data?.data ?? []) as College[]);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 200);

    return (): void => clearTimeout(handle);
  }, [query]);

  const choose = (college: College): void => {
    setSelected(college);
    setQuery('');
    setResults([]);
    setShowResults(false);
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (college.id != null) next.set('collegeId', String(college.id));
        else next.delete('collegeId');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  };

  const clear = (): void => {
    setSelected(null);
    setQuery('');
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        next.delete('collegeId');
        next.delete('currentlyAttendingCollege');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  };

  const toggleAttending = (checked: boolean): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        if (checked) next.set('currentlyAttendingCollege', 'true');
        else next.delete('currentlyAttendingCollege');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  };

  return (
    <section className='mb-4'>
      <h4 className='font-semibold mb-1'>Caregiver&apos;s university</h4>
      {selected ? (
        <div className='flex items-center justify-between border border-neutral-200 rounded px-2 py-1 text-sm mb-2'>
          <span>{formatCollege(selected)}</span>
          <Button
            buttonType={ButtonType.tertiary}
            id='clear-education'
            aria-label='Clear university filter'
            onClick={clear}
            className='text-neutral-500 hover:text-neutral-700 px-1'
          >
            &times;
          </Button>
        </div>
      ) : (
        <div className='relative mb-2'>
          <input
            type='text'
            id='search-education-text'
            aria-label='University name'
            placeholder='University name'
            autoComplete='off'
            className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm'
            value={query}
            onChange={(e): void => {
              setQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={(): void => setShowResults(true)}
            onBlur={(): void => {
              // Delay so clicks on results register before blur closes.
              setTimeout(() => setShowResults(false), 150);
            }}
          />
          {showResults && query.length >= 2 && (
            <ul className='absolute left-0 right-0 top-full z-10 bg-white border border-neutral-200 rounded shadow max-h-60 overflow-auto text-sm'>
              {loading && <li className='px-2 py-1 text-neutral-500'>Searching…</li>}
              {!loading && results.length === 0 && (
                <li className='px-2 py-1 text-neutral-500'>No matches</li>
              )}
              {!loading &&
                results.map((c) => (
                  <li key={c.id ?? `${c.name}-${c.city}`}>
                    <Button
                      buttonType={ButtonType.tertiary}
                      className='w-full text-left px-2 py-1 hover:bg-neutral-100'
                      onMouseDown={(e): void => {
                        // prevent blur before click registers
                        e.preventDefault();
                      }}
                      onClick={(): void => choose(c)}
                    >
                      {formatCollege(c)}
                    </Button>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
      <label className='flex items-center gap-2 text-sm'>
        <input
          id='currently-attending-toggle'
          type='checkbox'
          checked={currentlyAttending}
          onChange={(e): void => toggleAttending(e.target.checked)}
          disabled={!selected}
        />
        <span>Only currently attending</span>
      </label>
    </section>
  );
};
